import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, ChevronDown, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg, inr, today } from "@/lib/api";
import { PageHeader, StatCard, TableWrap, Empty, th, td, tr } from "@/components/common";

const TABS = [["summary", "Portfolio Summary"], ["turnover", "Monthly Turnover"], ["expense", "Monthly Expense"], ["own", "Own Vehicle Profit"], ["market", "Market Vehicle Profit"]];

function DL({ month, section, label = "" }) {
  const get = async (fmt) => {
    try {
      const r = await api.get("/profit-loss/export", { params: { month, section, fmt }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `Profit_Loss_${section}_${month}.${fmt}`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-1.5">
      <Button size="sm" variant="outline" data-testid={`pl-dl-${section}-xlsx`} onClick={() => get("xlsx")}><Download className="mr-1 h-3.5 w-3.5" />{label}Excel</Button>
      <Button size="sm" variant="outline" data-testid={`pl-dl-${section}-pdf`} onClick={() => get("pdf")}><Download className="mr-1 h-3.5 w-3.5" />{label}PDF</Button>
    </div>
  );
}

function Table({ head, rows, testid, right = [] }) {
  if (!rows.length) return <Empty testid={`${testid}-empty`} text="Is mahine koi entry nahi" />;
  return (
    <TableWrap testid={testid}>
      <table className="w-full text-sm">
        <thead><tr>{head.map((h, i) => <th key={h} className={`${th} ${right.includes(i) ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className={tr}>{r.map((c, j) => <td key={j} className={`${td} ${right.includes(j) ? "amount text-right" : ""}`}>{c}</td>)}</tr>)}</tbody>
      </table>
    </TableWrap>
  );
}

const pc = (v) => <span className={v < 0 ? "text-rose-600 font-semibold" : "text-emerald-700 font-semibold"}>{inr(v, 2)}</span>;

function ExpenseHeads({ d, month }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="card-base divide-y" data-testid="pl-heads">
      {d.head_labels.map(({ key, label }) => (
        <div key={key}>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <button className="flex flex-1 items-center gap-2 text-left text-sm font-semibold" data-testid={`pl-head-${key}`} onClick={() => setOpen(open === key ? null : key)}>
              {open === key ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{label}
              <span className="text-xs font-normal text-muted-foreground">({d.details[key].length})</span>
            </button>
            <span className="amount w-36 text-right font-semibold" data-testid={`pl-head-amt-${key}`}>{inr(d.heads[key], 2)}</span>
            <DL month={month} section={key} />
          </div>
          {open === key && <div className="px-4 pb-4"><Table testid={`pl-detail-${key}`} head={["Date", "Ref", "Vehicle", "Description", "Amount"]} right={[4]}
            rows={d.details[key].map((e) => [e.date, e.ref, e.vehicle, e.desc, inr(e.amount, 2)])} /></div>}
        </div>
      ))}
      <div className="flex justify-between px-4 py-3 text-sm font-bold"><span>Total Monthly Expense</span><span className="amount" data-testid="pl-total-expense">{inr(d.total_expense, 2)}</span></div>
    </div>
  );
}

function TrendChart({ trend }) {
  const mon = (m) => new Date(`${m}-01T00:00:00`).toLocaleString("en-IN", { month: "short", year: "2-digit" });
  const data = trend.map((t) => ({ label: mon(t.month), Turnover: t.turnover, Expense: t.total_expense, "Net Profit": t.net_profit }));
  const k = (v) => (Math.abs(v) >= 100000 ? `${(v / 100000).toFixed(1)}L` : Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}K` : v);
  return (
    <div className="card-base p-5" data-testid="pl-chart">
      <h3 className="mb-3 text-sm font-bold">Turnover vs Expense — last 12 months</h3>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: 0, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={k} tick={{ fontSize: 11 }} width={48} />
            <Tooltip formatter={(x) => inr(x)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Turnover" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Expense" fill="#f43f5e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Net Profit" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const PIE_COLORS = ["#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#14b8a6", "#ec4899", "#22c55e", "#6366f1", "#f97316", "#64748b"];

function ExpensePie({ d }) {
  const data = d.head_labels.map(({ key, label }, i) => ({ key, name: label, value: d.heads[key], color: PIE_COLORS[i % PIE_COLORS.length] })).filter((x) => x.value > 0);
  const total = d.total_expense || 1;
  return (
    <div className="card-base p-5" data-testid="pl-pie">
      <h3 className="mb-3 text-sm font-bold">Is mahine ka kharcha — bantwara</h3>
      {!data.length ? <Empty testid="pl-pie-empty" text="Is mahine koi kharcha nahi" /> : (
        <div className="grid items-center gap-4 md:grid-cols-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius="50%" outerRadius="85%" paddingAngle={1}>
                  {data.map((x) => <Cell key={x.key} fill={x.color} />)}
                </Pie>
                <Tooltip formatter={(v) => `${inr(v)} (${((v / total) * 100).toFixed(1)}%)`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 text-sm">
            {data.map((x) => (
              <div key={x.key} className="flex items-center justify-between gap-3" data-testid={`pl-pie-row-${x.key}`}>
                <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm" style={{ background: x.color }} />{x.name}</span>
                <span className="amount">{inr(x.value)} <span className="text-xs text-muted-foreground">{((x.value / total) * 100).toFixed(1)}%</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Summary({ d, month }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="pl-turnover" label="Total Monthly Turnover" value={inr(d.turnover)} hint={`incl. GST ${inr(d.gst)} · ${d.trips} LR`} />
        <StatCard testid="pl-expense" label="Total Monthly Expense" value={inr(d.total_expense)} accent="text-rose-600" delay={60} />
        <StatCard testid="pl-own" label="Own Vehicle Profit" value={inr(d.own_profit)} accent={d.own_profit < 0 ? "text-rose-600" : "text-emerald-600"} delay={120} />
        <StatCard testid="pl-market" label="Market Vehicle Profit" value={inr(d.market_profit)} accent={d.market_profit < 0 ? "text-rose-600" : "text-emerald-600"} delay={180} />
      </div>
      <div className="card-base flex flex-wrap items-center justify-between gap-3 p-5">
        <div><div className="text-xs font-semibold uppercase text-muted-foreground">Net Profit (Turnover excl. GST − Expense)</div>
          <div className="amount mt-1 text-2xl font-bold" data-testid="pl-net">{pc(d.net_profit)}</div></div>
        <DL month={month} section="all" label="Full Report " />
      </div>
      <TrendChart trend={d.trend} />
      <ExpensePie d={d} />
      <h3 className="text-sm font-bold">Monthly Expense Breakdown</h3>
      <ExpenseHeads d={d} month={month} />
      <h3 className="text-sm font-bold">12 Month Portfolio</h3>
      <Table testid="pl-trend" head={["Month", "LR", "Turnover", "Expense", "Net Profit"]} right={[2, 3, 4]}
        rows={d.trend.map((t) => [t.month, t.trips, inr(t.turnover), inr(t.total_expense), pc(t.net_profit)])} />
    </div>
  );
}

export default function ProfitLoss() {
  const { tab = "summary" } = useParams();
  const nav = useNavigate();
  const [month, setMonth] = useState(today().slice(0, 7));
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/profit-loss", { params: { month } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);
  return (
    <div className="space-y-6">
      <PageHeader title="Profit & Loss" subtitle="Mahine ka turnover, kharcha, apni aur market gaadi ka munafa" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">{TABS.map(([k, l]) => (
          <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} data-testid={`pl-tab-${k}`} onClick={() => nav(`/profit-loss/${k}`)}>{l}</Button>
        ))}</div>
        <Input type="month" className="w-44" data-testid="pl-month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
      </div>
      {!d ? <div className="text-sm text-muted-foreground">Loading…</div> : (
        <>
          {tab === "summary" && <Summary d={d} month={month} />}
          {tab === "turnover" && <div className="space-y-3"><div className="flex justify-between"><b className="text-sm">Turnover {inr(d.turnover, 2)} (GST {inr(d.gst, 2)})</b><DL month={month} section="turnover" /></div>
            <Table testid="pl-turnover-table" head={["Date", "LR", "Party", "Vehicle", "Freight", "GST", "Total"]} right={[4, 5, 6]}
              rows={d.turnover_rows.map((x) => [x.date, x.lr_number, x.party, x.vehicle, inr(x.freight, 2), inr(x.gst, 2), inr(x.total, 2)])} /></div>}
          {tab === "expense" && <div className="space-y-3"><div className="flex justify-end"><DL month={month} section="expense" label="All Expense " /></div><ExpenseHeads d={d} month={month} /></div>}
          {tab === "own" && <div className="space-y-3"><div className="flex justify-between"><b className="text-sm">Own Vehicle Profit {pc(d.own_profit)}</b><DL month={month} section="own" /></div>
            <Table testid="pl-own-table" head={["Vehicle", "Trips", "Revenue", "Driver", "Trip Bills", "Total Cost", "Net"]} right={[2, 3, 4, 5, 6]}
              rows={d.own.map((x) => [x.vehicle_number, x.trips, inr(x.revenue, 2), inr(x.driver_cost, 2), inr(x.driver_bills, 2), inr(x.total_cost, 2), pc(x.net)])} /></div>}
          {tab === "market" && <div className="space-y-3"><div className="flex justify-between"><b className="text-sm">Market Vehicle Profit {pc(d.market_profit)}</b><DL month={month} section="market" /></div>
            <Table testid="pl-market-table" head={["Date", "LR", "Vehicle", "Party", "Freight", "Hire", "Trip Exp", "Brokerage", "Profit"]} right={[4, 5, 6, 7, 8]}
              rows={d.market.map((x) => [x.date, x.lr_number, x.vehicle, x.party, inr(x.freight, 2), inr(x.hire, 2), inr(x.expense, 2), inr(x.brokerage, 2), pc(x.profit)])} /></div>}
        </>
      )}
    </div>
  );
}
